import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { DataTableColumn } from "@/components/ui/DataTable/types";
import { NeedsAttentionRow } from "@/lib/api/risk/brokerage/tt/v2";

export const DEFAULT_COLUMN_VISIBILITY = {
  actions: true,
  exchange_event_time: true,
  trading_account_name: true,
  user_name: true,
  instrument_name: true,
  buy_sell_type: true,
  order_quantity: true,
  working_quantity: true,
  filled_quantity: true,
  reasons: true,
  text_a: true,
  text_tt: true,
  text_b: false,
  text_c: false,
  remaining_quantity: false,
  limit_price: false,
};

/**
 * Builds the column list for the TT v2 needs-attention table.
 * The `actions` and `reasons` columns are wired by the table component (it
 * supplies the cell renderers) so this file stays free of JSX.
 */
export function buildBaseColumns(): DataTableColumn<NeedsAttentionRow>[] {
  return [
    {
      id: "exchange_event_time",
      accessorKey: "exchange_event_time",
      header: "First Event",
      size: 140,
      minSize: 120,
      columnFilterType: "date",
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v) =>
        moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD hh:mm a"),
      filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
      cell: (info) =>
        moment
          .tz(info.getValue() as string, CommonDestinationTimezone)
          .format("MM/DD/YYYY hh:mm a"),
    },
    {
      id: "trading_account_name",
      accessorKey: "trading_account_name",
      header: "Account",
      size: 160,
      minSize: 120,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "user_name",
      accessorKey: "user_name",
      header: "User",
      size: 140,
      minSize: 100,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "instrument_name",
      accessorKey: "instrument_name",
      header: "Instrument",
      size: 220,
      minSize: 150,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "buy_sell_type",
      accessorKey: "buy_sell_type",
      header: "B/S",
      size: 70,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "order_quantity",
      accessorKey: "order_quantity",
      header: "Order",
      size: 80,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "working_quantity",
      accessorKey: "working_quantity",
      header: "Working",
      size: 80,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "filled_quantity",
      accessorKey: "filled_quantity",
      header: "Filled",
      size: 80,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "remaining_quantity",
      accessorKey: "remaining_quantity",
      header: "Remaining",
      size: 90,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "limit_price",
      accessorKey: "limit_price",
      header: "Limit Price",
      size: 100,
      minSize: 80,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "text_a",
      accessorKey: "text_a",
      header: "Text A",
      size: 120,
      minSize: 80,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "text_b",
      accessorKey: "text_b",
      header: "Text B",
      size: 120,
      minSize: 80,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "text_c",
      accessorKey: "text_c",
      header: "Text C",
      size: 120,
      minSize: 80,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
    {
      id: "text_tt",
      accessorKey: "text_tt",
      header: "Text TT",
      size: 120,
      minSize: 80,
      cell: (info) => (info.getValue() as string | null) ?? "-",
    },
  ];
}
